import { cleanedPhoneNumber } from "@/services/api-data-mapping";
import { convertFirstChar, getCookie } from "@/services/generic.methods";
import { useEffect, useRef } from "react";
import { useCRMDataClient } from "@/services/data-client/DataClientContext";

const getFieldMetaFromFormJson = (formJson, fieldName) => {
  if (!formJson?.sections) return null;
  for (const section of formJson.sections) {
    const field = section?.fields?.find((f) => f.name === fieldName);
    if (field) return field;
  }
  return null;
};

const UseDynamicForm = (props) => {
  const {
    apiData,
    FormikForm,
    invalidFields,
    setInvalidFields,
    formJson,
    requiredFieldNames = [],
    validatingFields = {}
  } = props;
  const { client, companyKey } = useCRMDataClient();
  const serverDataRef = useRef({});
  const skipInitialValidateRef = useRef(
    !!(props.draftValues && Object.keys(props.draftValues).length > 0),
  );
  useEffect(() => {
    if (skipInitialValidateRef.current) {
      skipInitialValidateRef.current = false;
      return;
    }
    FormikForm.validateForm();
  }, [invalidFields]);

  useEffect(() => {
    serverDataRef.current = apiData;
  }, [apiData]);

  const resetField = (name) => {
    setInvalidFields((prevInvalidFields) => {
      delete prevInvalidFields[name];
      return {
        ...prevInvalidFields,
      };
    });
  };

  // const getSSRError = (name) =>
  //   `${convertFirstChar(name, false)}${
  //     name === "phone" ? " number" : ""
  //   } already in use for Lead ID: `;
  const getSSRError = (name) =>
    `${convertFirstChar(name, false)} already in use for Lead ID: `;

  const validateServerSide = async ({
    e,
    rules,
    executeQuery,
    countryCode,
    errors = [], // given the updated formik state
  }) => {
    const { gqlValidate = null } = rules;
    const { name = "", value = "" } = e.target;
    // let hasFieldError = errors.length > 0;
    // let hasFieldErrorSSR = errors.includes(getSSRError(name));
    let cleanedVal = value.toString();
    let payload = {};

    if (name === "phone") {
      cleanedVal = cleanedPhoneNumber(cleanedVal);

      if (cleanedVal) payload["countryCode"] = countryCode;
      // cleanedVal = `${countryCode}${value
      //   .toString()
      //   .replace(/[^a-zA-Z0-9]/g, "")}`;
    }

    payload[name] = cleanedVal;

    if (
      cleanedVal.length &&
      cleanedVal !== serverDataRef?.current?.[name] &&
      // (!hasFieldError && hasFieldErrorSSR) &&
      gqlValidate
    ) {
      if (client && client.leads && typeof client.leads.validateLeadField === "function") {
        try {
          const activeCompanyKey = companyKey || getCookie("companyKey");
          const context = { companyKey: activeCompanyKey, executeQuery, countryCode };
          return client.leads.validateLeadField(name, cleanedVal, context).then((lead) => {
            const isMatch = lead && (name === "phone" ? true : lead[name]?.toLowerCase() === cleanedVal.toLowerCase());
            const currentId = apiData?.id;
            const conflictId = lead?.id || lead?.leadId;
            const isSelf = currentId && (conflictId === currentId || conflictId === +currentId);
            if (isMatch && !isSelf) {
              setInvalidFields((prevInvalidFields) => ({
                ...prevInvalidFields,
                [name]: `${getSSRError(name, conflictId)} ${conflictId}`,
              }));
              FormikForm?.setFieldTouched(name, true);
              console.log(`Invalid ${name}:: `, lead[name] === cleanedVal);
              return true;
            } else {
              resetField(name);
            }
            return false;
          });
        } catch (error) {
          console.error("Validate Error:: ", error);
          return false;
        }
      }
    } else if (cleanedVal === serverDataRef.current?.[name]) {
      resetField(name);
    }
    return false;
  };

  const getRequiredFieldErrors = () => {
    const errors = {};
    requiredFieldNames.forEach((fieldName) => {
      const value = FormikForm?.values?.[fieldName];
      if (!value || !String(value).trim()) {
        const fieldMeta = getFieldMetaFromFormJson(formJson, fieldName);
        const title = fieldMeta?.title || convertFirstChar(fieldName, false);
        errors[fieldName] = `${title} is required`;
      }
    });
    return errors;
  };

  const touchFields = (fieldNames) => {
    if (!fieldNames?.length) return;
    const touchedUpdate = fieldNames.reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      { ...FormikForm.touched },
    );
    FormikForm.setTouched(touchedUpdate, false);
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    if (Object.keys(validatingFields).length > 0) {
      return; // prevent submit if validation is in progress
    }
    // Manually validate the form
    FormikForm.validateForm().then((errors) => {
      // Check if there are any errors
      const requiredErrors = getRequiredFieldErrors();
      const allErrors = {
        ...errors,
        ...invalidFields,
        ...requiredErrors,
      };
      if (Object.keys(allErrors).length === 0) {
        // No errors, proceed with form submission
        FormikForm.handleSubmit();
      } else {
        const fieldsToTouch = [
          ...new Set([...requiredFieldNames, ...Object.keys(allErrors)]),
        ];
        touchFields(fieldsToTouch);
        FormikForm.setErrors(allErrors);
      }
    });
  };

  return { validateServerSide, onFormSubmit };
};

export default UseDynamicForm;
